import { Check } from "lucide-react";
import type { CoachingVisibility } from "@/lib/types";
import { TkticSelect } from "@/components/ui/TkticSelect";

/**
 * A6 — bulk-action toolbar for the Feedback Library list pages
 * (Notes / Observations / Clips). Appears once at least one row is
 * selected: shows the count, a "Set visibility" select that applies
 * to every selected row, a Delete action, and a Clear. The pages own
 * the selection state + the actual mutations; this is pure
 * presentation.
 *
 * The visibility options mirror the composer's closed set. When
 * "Player + family" is chosen and one or more selected rows have NO
 * tagged players, the parent passes `untaggedWarning` so the coach is
 * told those rows won't reach a player (player-visibility with zero
 * tags is invisible to viewers — same gotcha the composer guards).
 */

const VISIBILITY_OPTIONS: ReadonlyArray<{
  value: CoachingVisibility;
  label: string;
}> = [
  { value: "team", label: "Team" },
  { value: "player", label: "Player + family" },
  { value: "private", label: "Coach-only" },
  { value: "unlisted", label: "Unlisted" },
];

export type BulkSelectionBarProps = {
  count: number;
  /** Disable controls while a bulk op is in flight. */
  busy?: boolean;
  /** Non-null when "Player + family" is the pending choice AND some
   * selected rows have zero tagged players — shown as an inline
   * caution under the bar. */
  untaggedWarning?: string | null;
  onSetVisibility: (visibility: CoachingVisibility) => void;
  onDelete: () => void;
  onClear: () => void;
  /** Distinguishes the Notes vs Clips bar in tests. */
  testIdPrefix: string;
};

export function BulkSelectionBar({
  count,
  busy = false,
  untaggedWarning,
  onSetVisibility,
  onDelete,
  onClear,
  testIdPrefix,
}: BulkSelectionBarProps) {
  if (count === 0) return null;
  return (
    <div
      className="flex flex-col gap-2 rounded border border-rule bg-elevated p-2"
      data-testid={`${testIdPrefix}-bulk-bar`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span
          className="font-display text-sm text-ink"
          data-testid={`${testIdPrefix}-bulk-count`}
        >
          {count} selected
        </span>
        <span className="text-ink-muted">·</span>
        <label className="flex items-center gap-2 text-xs text-ink-muted">
          Set visibility
          <span className="w-40">
            <TkticSelect
              value=""
              onChange={(next) =>
                onSetVisibility(next as CoachingVisibility)
              }
              ariaLabel="Set visibility for selected"
              data-testid={`${testIdPrefix}-bulk-visibility`}
              disabled={busy}
              options={[
                { value: "", label: "Choose…", disabled: true },
                ...VISIBILITY_OPTIONS,
              ]}
            />
          </span>
        </label>
        <button
          type="button"
          onClick={onDelete}
          disabled={busy}
          data-testid={`${testIdPrefix}-bulk-delete`}
          className="rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink-muted transition-colors hover:border-destructive hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
        >
          Delete
        </button>
        <button
          type="button"
          onClick={onClear}
          disabled={busy}
          data-testid={`${testIdPrefix}-bulk-clear`}
          className="ml-auto rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink-muted transition-colors hover:bg-field disabled:opacity-40"
        >
          Clear
        </button>
      </div>
      {untaggedWarning ? (
        <p
          className="text-xs text-flag"
          data-testid={`${testIdPrefix}-bulk-untagged-warning`}
        >
          {untaggedWarning}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The per-row selection toggle — a styled checkbox (no native
 * `<input type=checkbox>` per the no-native-chrome rule). `aria-pressed`
 * carries the state for assistive tech.
 */
export function SelectToggle({
  selected,
  onToggle,
  label,
  testid,
}: {
  selected: boolean;
  onToggle: () => void;
  label: string;
  testid: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      aria-label={label}
      title={label}
      data-testid={testid}
      className={[
        "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors",
        selected
          ? "border-accent bg-accent text-accent-ink"
          : "border-rule-strong bg-transparent text-transparent hover:bg-field",
      ].join(" ")}
    >
      <Check size={13} strokeWidth={3} aria-hidden="true" />
    </button>
  );
}
